import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth, canViewActual, hasRole } from "../lib/auth";
import { PageHeader, Section, StatCard } from "../components/Primitives";
import Pagination, { usePagination } from "../components/Pagination";
import { ShieldAlert, Plus, Play, Pause, Trash2, RefreshCcw, Mail, X } from "lucide-react";
import { toast } from "sonner";
import { Navigate } from "react-router-dom";
import { useApiLoading, LoadingRow } from "../components/TableLoader";

const fmtMoney = (v) => `$${(v || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const severityClass = (s) => {
    if (s === "critical") return "bg-red-500/10 text-red-400 border-red-500/20";
    if (s === "warning") return "bg-amber-500/10 text-amber-400 border-amber-500/20";
    return "bg-cyan-500/10 text-cyan-400 border-cyan-500/20";
};

export default function MarginAlerts() {
    const { user } = useAuth();
    const [rules, setRules] = useState([]);
    const apiLoading = useApiLoading();
    const [events, setEvents] = useState([]);
    const [agencies, setAgencies] = useState([]);
    const [advertisers, setAdvertisers] = useState([]);
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState({
        name: "Margin below 30%",
        threshold_pct: 30,
        severity: "warning",
        recipients: user?.email || "",
        cooldown_minutes: 1440,
        agency_id: "",
        advertiser_id: "",
    });

    const loadAll = async () => {
        const [r, e] = await Promise.all([
            api.get("/margin-alerts/rules"),
            api.get("/margin-alerts/events"),
        ]);
        setRules(r.data);
        setEvents(e.data);
    };

    useEffect(() => {
        if (!canViewActual(user)) return;
        loadAll();
        if (user.role === "super_admin") api.get("/agencies").then((r) => setAgencies(r.data));
        if (hasRole(user, "super_admin", "admin")) api.get("/advertisers").then((r) => setAdvertisers(r.data));
    }, [user]); // eslint-disable-line

    const rulesPager = usePagination(rules, 25);
    const eventsPager = usePagination(events, 25);

    if (!canViewActual(user)) {
        return <Navigate to="/dashboard" replace />;
    }

    const openCreate = () => {
        setEditing(null);
        setForm({
            name: "Margin below 30%",
            threshold_pct: 30,
            severity: "warning",
            recipients: user.email || "",
            cooldown_minutes: 1440,
            agency_id: "",
            advertiser_id: "",
        });
        setOpen(true);
    };
    const openEdit = (r) => {
        setEditing(r);
        setForm({
            name: r.name,
            threshold_pct: r.threshold_pct,
            severity: r.severity,
            recipients: r.recipients,
            cooldown_minutes: r.cooldown_minutes,
            agency_id: r.agency_id || "",
            advertiser_id: r.advertiser_id || "",
        });
        setOpen(true);
    };

    const submit = async (e) => {
        e.preventDefault();
        const payload = {
            ...form,
            threshold_pct: parseFloat(form.threshold_pct),
            cooldown_minutes: parseInt(form.cooldown_minutes, 10),
            agency_id: form.agency_id || null,
            advertiser_id: form.advertiser_id || null,
        };
        try {
            if (editing) {
                await api.put(`/margin-alerts/rules/${editing.id}`, payload);
                toast.success("Rule updated");
            } else {
                await api.post("/margin-alerts/rules", payload);
                toast.success("Rule created");
            }
            setOpen(false);
            loadAll();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed");
        }
    };

    const toggle = async (id) => {
        await api.post(`/margin-alerts/rules/${id}/toggle`);
        loadAll();
    };
    const remove = async (id) => {
        if (!window.confirm("Delete this margin alert rule?")) return;
        await api.delete(`/margin-alerts/rules/${id}`);
        loadAll();
    };
    const checkNow = async (id) => {
        try {
            const url = id ? `/margin-alerts/check?rule_id=${id}` : "/margin-alerts/check";
            const { data } = await api.post(url);
            toast.success(`Checked ${data.checked_rules} rule(s) · ${data.new_events} new event(s)`);
            loadAll();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Check failed");
        }
    };

    const totalEvents = events.length;
    const criticalCount = events.filter((e) => e.severity === "critical").length;
    const worst = events[0];

    return (
        <div data-testid="margin-alerts-page">
            <PageHeader
                eyebrow="Internal · P&L protection"
                title="Margin alerts"
                subtitle="Get notified the moment a campaign's projected margin drops below your floor. Visible to super admins and admins with actual-cost access only."
                actions={
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => checkNow()}
                            className="inline-flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium border border-zinc-300 hover:bg-zinc-100 transition-colors"
                            data-testid="margin-check-all-btn"
                        >
                            <RefreshCcw className="h-4 w-4" /> Check now
                        </button>
                        <button
                            onClick={openCreate}
                            className="inline-flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium"
                            style={{ background: "var(--wl-primary-hex)", color: "#ffffff" }}
                            data-testid="margin-create-rule-btn"
                        >
                            <Plus className="h-4 w-4" /> New rule
                        </button>
                    </div>
                }
            />

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
                <StatCard label="Active rules" value={rules.filter((r) => r.is_active).length} hint={`${rules.length} total`} testId="margin-stat-rules" />
                <StatCard label="Recent events" value={totalEvents} accent testId="margin-stat-events" />
                <StatCard label="Critical" value={criticalCount} hint="severity = critical" testId="margin-stat-critical" />
                <StatCard label="Worst margin" value={worst ? `${worst.margin_pct.toFixed(1)}%` : "—"} hint={worst ? worst.campaign_name : ""} testId="margin-stat-worst" />
            </div>

            <Section title="Rules" testId="section-margin-rules">
                <div className="bg-white border border-zinc-200 overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="bg-zinc-100 border-b border-zinc-200">
                                <th className="overline text-left p-3">Name</th>
                                <th className="overline text-left p-3">Threshold</th>
                                <th className="overline text-left p-3">Severity</th>
                                <th className="overline text-left p-3">Scope</th>
                                <th className="overline text-left p-3">Recipients</th>
                                <th className="overline text-left p-3">Last checked</th>
                                <th className="overline text-left p-3">Status</th>
                                <th className="overline text-right p-3">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rulesPager.slice.map((r) => (
                                <tr key={r.id} className="border-b border-zinc-200 last:border-0">
                                    <td className="p-3">
                                        <button onClick={() => openEdit(r)} className="text-zinc-900 hover:wl-accent-text text-left" data-testid={`edit-rule-${r.id}`}>{r.name}</button>
                                    </td>
                                    <td className="p-3 tabular-nums wl-accent-text font-medium">&lt; {r.threshold_pct.toFixed(0)}%</td>
                                    <td className="p-3">
                                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] uppercase border ${severityClass(r.severity)}`}>
                                            <ShieldAlert className="h-3 w-3" />{r.severity}
                                        </span>
                                    </td>
                                    <td className="p-3 text-xs text-zinc-500">{r.advertiser_id ? "Advertiser" : r.agency_id ? "Agency" : "All"}</td>
                                    <td className="p-3 text-xs text-zinc-500 truncate max-w-[200px]">{r.recipients}</td>
                                    <td className="p-3 text-xs text-zinc-500">{r.last_checked_at ? new Date(r.last_checked_at).toLocaleString() : "—"}</td>
                                    <td className="p-3">
                                        <span className={`px-2 py-0.5 rounded text-[10px] uppercase ${r.is_active ? "bg-emerald-500/10 text-emerald-400 border border-emerald-500/20" : "bg-zinc-200 text-zinc-500"}`}>
                                            {r.is_active ? "active" : "paused"}
                                        </span>
                                    </td>
                                    <td className="p-3 text-right">
                                        <div className="inline-flex items-center gap-3 text-xs">
                                            <button onClick={() => checkNow(r.id)} className="inline-flex items-center gap-1 text-zinc-700 hover:wl-accent-text" data-testid={`check-rule-${r.id}`}>
                                                <RefreshCcw className="h-3.5 w-3.5" />check
                                            </button>
                                            <button onClick={() => toggle(r.id)} className="inline-flex items-center gap-1 text-zinc-500 hover:wl-accent-text" data-testid={`toggle-rule-${r.id}`}>
                                                {r.is_active ? <><Pause className="h-3.5 w-3.5" />pause</> : <><Play className="h-3.5 w-3.5" />resume</>}
                                            </button>
                                            <button onClick={() => remove(r.id)} className="text-red-400 hover:text-red-300" data-testid={`delete-rule-${r.id}`}>
                                                <Trash2 className="h-3.5 w-3.5" />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                            {!rules.length && (
                                apiLoading ? <LoadingRow colSpan={8} /> :
                                <tr><td colSpan={8} className="p-10 text-center text-zinc-500">
                                    No margin alert rules yet. Click <span className="wl-accent-text">New rule</span> to create one.
                                </td></tr>
                            )}
                        </tbody>
                    </table>
                    {rules.length > 0 && (
                        <Pagination
                            page={rulesPager.page}
                            totalPages={rulesPager.totalPages}
                            pageSize={rulesPager.pageSize}
                            range={rulesPager.range}
                            setPage={rulesPager.setPage}
                            setPageSize={rulesPager.setPageSize}
                            dataTestId="margin-rules-pagination"
                        />
                    )}
                </div>
            </Section>

            <Section title="Recent events" testId="section-margin-events">
                <div className="bg-white border border-zinc-200 overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="bg-zinc-100 border-b border-zinc-200">
                                <th className="overline text-left p-3">When</th>
                                <th className="overline text-left p-3">Campaign</th>
                                <th className="overline text-left p-3">Advertiser</th>
                                <th className="overline text-right p-3">Margin</th>
                                <th className="overline text-right p-3">Campaign Budget</th>
                                <th className="overline text-right p-3">Campaign Spends</th>
                                <th className="overline text-left p-3">Severity</th>
                                <th className="overline text-left p-3">Email</th>
                            </tr>
                        </thead>
                        <tbody>
                            {eventsPager.slice.map((e) => (
                                <tr key={e.id} className="border-b border-zinc-200 last:border-0">
                                    <td className="p-3 text-xs text-zinc-500">{new Date(e.created_at).toLocaleString()}</td>
                                    <td className="p-3 text-zinc-900 truncate max-w-[260px]" title={e.campaign_name}>{e.campaign_name}</td>
                                    <td className="p-3 text-zinc-700">{e.advertiser_name || "—"}</td>
                                    <td className="p-3 text-right">
                                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium border bg-red-500/10 text-red-400 border-red-500/20 tabular-nums">
                                            <ShieldAlert className="h-3 w-3" />{e.margin_pct.toFixed(1)}%
                                            <span className="opacity-70">({fmtMoney(e.margin_amount)})</span>
                                        </span>
                                    </td>
                                    <td className="p-3 text-right tabular-nums text-zinc-800">{fmtMoney(e.display_spend)}</td>
                                    <td className="p-3 text-right tabular-nums text-zinc-500">{fmtMoney(e.actual_spend)}</td>
                                    <td className="p-3">
                                        <span className={`px-2 py-0.5 rounded text-[10px] uppercase border ${severityClass(e.severity)}`}>{e.severity}</span>
                                    </td>
                                    <td className="p-3">
                                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] uppercase ${e.email_status === "sent" ? "bg-emerald-500/10 text-emerald-400 border border-emerald-500/20" : e.email_status === "mocked" ? "bg-amber-500/10 text-amber-400 border border-amber-500/20" : e.email_status === "failed" ? "bg-red-500/10 text-red-400 border border-red-500/20" : "bg-zinc-200 text-zinc-500"}`}>
                                            <Mail className="h-3 w-3" />{e.email_status}
                                        </span>
                                    </td>
                                </tr>
                            ))}
                            {!events.length && (
                                apiLoading ? <LoadingRow colSpan={8} /> :
                                <tr><td colSpan={8} className="p-10 text-center text-zinc-500">No alerts fired yet. Click "Check now" to evaluate active rules.</td></tr>
                            )}
                        </tbody>
                    </table>
                    {events.length > 0 && (
                        <Pagination
                            page={eventsPager.page}
                            totalPages={eventsPager.totalPages}
                            pageSize={eventsPager.pageSize}
                            range={eventsPager.range}
                            setPage={eventsPager.setPage}
                            setPageSize={eventsPager.setPageSize}
                            dataTestId="margin-events-pagination"
                        />
                    )}
                </div>
            </Section>

            {open && (
                <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
                    <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="bg-zinc-50 border border-zinc-200 w-full max-w-lg p-6 rounded-md" data-testid="margin-rule-modal">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="font-display text-xl">{editing ? "Edit rule" : "New margin alert rule"}</h3>
                            <button type="button" onClick={() => setOpen(false)}><X className="h-4 w-4 text-zinc-500" /></button>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div className="col-span-2">
                                <label className="overline">Rule name</label>
                                <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full mt-1 px-3 py-2 bg-zinc-100 border border-zinc-200 rounded" data-testid="rule-name-input" />
                            </div>
                            <div>
                                <label className="overline">Margin floor (%)</label>
                                <input required type="number" step="0.1" value={form.threshold_pct} onChange={(e) => setForm({ ...form, threshold_pct: e.target.value })} className="w-full mt-1 px-3 py-2 bg-zinc-100 border border-zinc-200 rounded tabular-nums" data-testid="rule-threshold-input" />
                                <p className="text-[11px] text-zinc-500 mt-1">Alert fires when margin% &lt; this.</p>
                            </div>
                            <div>
                                <label className="overline">Severity</label>
                                <select value={form.severity} onChange={(e) => setForm({ ...form, severity: e.target.value })} className="w-full mt-1 px-3 py-2 bg-zinc-100 border border-zinc-200 rounded">
                                    <option value="info">Info</option>
                                    <option value="warning">Warning</option>
                                    <option value="critical">Critical</option>
                                </select>
                            </div>
                            {user.role === "super_admin" && (
                                <div>
                                    <label className="overline">Agency scope</label>
                                    <select value={form.agency_id} onChange={(e) => setForm({ ...form, agency_id: e.target.value })} className="w-full mt-1 px-3 py-2 bg-zinc-100 border border-zinc-200 rounded">
                                        <option value="">All agencies</option>
                                        {agencies.map((a) => (<option key={a.id} value={a.id}>{a.name}</option>))}
                                    </select>
                                </div>
                            )}
                            <div>
                                <label className="overline">Advertiser scope</label>
                                <select value={form.advertiser_id} onChange={(e) => setForm({ ...form, advertiser_id: e.target.value })} className="w-full mt-1 px-3 py-2 bg-zinc-100 border border-zinc-200 rounded">
                                    <option value="">All advertisers in scope</option>
                                    {advertisers.map((a) => (<option key={a.id} value={a.id}>{a.name}</option>))}
                                </select>
                            </div>
                            <div className="col-span-2">
                                <label className="overline">Recipients (comma-separated emails)</label>
                                <input required value={form.recipients} onChange={(e) => setForm({ ...form, recipients: e.target.value })} className="w-full mt-1 px-3 py-2 bg-zinc-100 border border-zinc-200 rounded" data-testid="rule-recipients-input" />
                            </div>
                            <div>
                                <label className="overline">Cooldown (minutes)</label>
                                <input type="number" value={form.cooldown_minutes} onChange={(e) => setForm({ ...form, cooldown_minutes: e.target.value })} className="w-full mt-1 px-3 py-2 bg-zinc-100 border border-zinc-200 rounded tabular-nums" />
                                <p className="text-[11px] text-zinc-500 mt-1">Same campaign won't re-fire within this window.</p>
                            </div>
                        </div>
                        <div className="flex justify-end gap-2 mt-5">
                            <button type="button" onClick={() => setOpen(false)} className="px-3 py-2 text-sm text-zinc-500">Cancel</button>
                            <button type="submit" className="px-4 py-2 rounded text-sm font-medium" style={{ background: "var(--wl-primary-hex)", color: "#ffffff" }} data-testid="rule-submit-btn">
                                {editing ? "Save changes" : "Create rule"}
                            </button>
                        </div>
                    </form>
                </div>
            )}
        </div>
    );
}
